import React from "react";
import {
  Briefcase,
  DollarSign,
  Building,
  AlertCircle,
  MapPin,
  Users,
  Calendar,
  FileText,
  Star,
  BarChart3,
  TrendingUp,
  Clock,
  Target,
  GraduationCap,
  FileCheck,
  Repeat,
  Shield,
  ShieldCheck,
} from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import ManagerAutocomplete from "./ManagerAutocomplete";
import { getWorkflowApprovalLabel } from "../Constant/EmployeeActions.config";
import { apiFetch } from "../../../context/AuthContext";

const API_URL = process.env.REACT_APP_BASE_URL;

// Module-level (not component state, since this component early-returns
// before any hooks could run) debounce handle for the gross<->net live
// preview call — see handleSalaryFieldChange below.
let salaryPreviewTimer = null;

const ActionFormFields = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  selectedAction,
  formData,
  handleInputChange,
  handleDropdownChange,
  departments,
  departmentManagers,
  selectedEmployee,
  ACTION_TYPES,
  workflowDefinitions,
  // Manager view: salary is admin / admin-level information. The salary
  // inputs and the "Current → New" preview are not rendered, and the page
  // doesn't send salary fields, so the employee's salary stays as it is.
  hideSalary = false,
}) => {
  if (!selectedAction) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyStateIcon}>
          <Target size={60} />
        </div>
        <h3 style={styles.emptyStateTitle}>{t.selectAction}</h3>
        <p style={styles.emptyStateText}>{t.selectActionToProceed}</p>
      </div>
    );
  }

  // Gross/Net salary are mutually derived: whichever one the admin types
  // into becomes `salaryInputSource` (sent to the backend as the
  // authoritative figure once the action is approved and applied to the
  // employee); the other field locks and shows a live preview fetched from
  // the server — same formulas the approval itself will use, never
  // duplicated here. Plain function (not a hook) because this component
  // early-returns above before any hooks could run.
  const handleSalaryFieldChange = (fieldName, value) => {
    const source = fieldName === "newGrossSalary" ? "gross" : "net";
    const otherField = source === "gross" ? "newSalary" : "newGrossSalary";
    if (salaryPreviewTimer) clearTimeout(salaryPreviewTimer);

    // Clearing the field releases the lock so the admin can type in either one.
    if (value === "" || value === null) {
      handleDropdownChange(fieldName, "");
      handleDropdownChange(otherField, "");
      handleDropdownChange("salaryInputSource", "");
      return;
    }

    handleDropdownChange(fieldName, value);
    handleDropdownChange("salaryInputSource", source);
    if (isNaN(Number(value))) return;

    scheduleSalaryPreview(source, value, currentInsuranceInput());
  };

  // Insurance salary drives the tax (the employee's 11% share comes off
  // before tax), so gross/net are always previewed against the insurance
  // salary typed HERE — falling back to the employee's current one.
  const currentInsuranceInput = (override) => {
    const raw = override !== undefined ? override : formData.newInsuranceSalary;
    if (raw === "" || raw === null || raw === undefined || isNaN(Number(raw))) {
      return selectedEmployee?.insuranceSalary ?? null;
    }
    return Number(raw);
  };

  const scheduleSalaryPreview = (source, value, insuranceSalary) => {
    if (salaryPreviewTimer) clearTimeout(salaryPreviewTimer);
    salaryPreviewTimer = setTimeout(async () => {
      try {
        const response = await apiFetch(`${API_URL}/employees/salary-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grossSalary: source === "gross" ? value : null,
            netSalary: source === "net" ? value : null,
            salaryInputSource: source,
            insuranceSalary: insuranceSalary ?? null,
          }),
        });
        const data = await response.json();
        if (data?.success) {
          if (source === "gross") handleDropdownChange("newSalary", data.netSalary ?? "");
          else handleDropdownChange("newGrossSalary", data.grossSalary ?? "");
        }
      } catch (err) {
        console.error("Salary preview failed:", err);
      }
    }, 400);
  };

  // New insurance salary: re-derive the locked side. If the admin typed a
  // gross, net is recalculated; otherwise net is kept (typed or current) and
  // gross is recalculated — same rule the server applies on save.
  const handleInsuranceChange = (value) => {
    handleDropdownChange("newInsuranceSalary", value);
    if (value !== "" && isNaN(Number(value))) return;
    const insurance = currentInsuranceInput(value);
    if (formData.salaryInputSource === "gross" && formData.newGrossSalary !== "" && !isNaN(Number(formData.newGrossSalary))) {
      scheduleSalaryPreview("gross", formData.newGrossSalary, insurance);
      return;
    }
    const net = formData.newSalary !== "" && !isNaN(Number(formData.newSalary))
      ? formData.newSalary
      : selectedEmployee?.netSalary;
    if (net === undefined || net === null || net === "") return;
    if (formData.newSalary === "" || formData.newSalary === null) handleDropdownChange("newSalary", net);
    scheduleSalaryPreview("net", net, insurance);
  };

  const actionConfig = ACTION_TYPES.find((a) => a.id === selectedAction);

  const renderActionSpecificFields = () => {
    switch (selectedAction) {
      case "promote":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Briefcase size={14} />
                {t.newPosition} *
              </label>
              <input
                type="text"
                name="newPosition"
                value={formData.newPosition}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterNewPosition}
                required
              />
            </div>
            {!hideSalary && (
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <DollarSign size={14} />
                {t.newSalary} *
              </label>
              <input
                type="number"
                name="newSalary"
                value={formData.newSalary}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterNewSalary}
                required
                min="0"
                step="100"
              />
              {selectedEmployee && formData.newSalary && (
                <div
                  style={{
                    fontSize: "clamp(11px, 1.6vw, 12px)",
                    color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                    marginTop: "6px",
                    fontWeight: "600",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t.currentSalary}: {t.egp}{" "}
                  {selectedEmployee.netSalary?.toLocaleString()} →{" "}
                  {t.newSalaryAmount}: {t.egp}{" "}
                  {parseFloat(formData.newSalary).toLocaleString()}
                  {formData.newSalary > selectedEmployee.netSalary && (
                    <span
                      style={{
                        color: "#10b981",
                        marginLeft: isRTL ? "0" : "8px",
                        marginRight: isRTL ? "8px" : "0",
                      }}
                    >
                      (+
                      {(
                        ((formData.newSalary - selectedEmployee.netSalary) /
                          selectedEmployee.netSalary) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  )}
                </div>
              )}
            </div>
            )}
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Building size={14} />
                {t.newDepartment}
              </label>
              <ReusableDropdown
                items={departments.map((dept) => ({ id: dept, name: dept }))}
                value={formData.newDepartment}
                onChange={(value) => handleDropdownChange("newDepartment", value)}
                placeholder={t.keepCurrentDept}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("newDepartment", "")}
              />
            </div>
          </>
        );

      case "demote":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Briefcase size={14} />
                {t.newPosition} *
              </label>
              <input
                type="text"
                name="newPosition"
                value={formData.newPosition}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterNewPosition}
                required
              />
            </div>
            {!hideSalary && (
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <DollarSign size={14} />
                {t.newSalary} *
              </label>
              <input
                type="number"
                name="newSalary"
                value={formData.newSalary}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterNewSalary}
                required
                min="0"
                step="100"
              />
            </div>
            )}
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <AlertCircle size={14} />
                {t.demotionReasonCategory} *
              </label>
              <ReusableDropdown
                items={[
                  { id: "", name: t.selectReason },
                  { id: "performance", name: t.performanceIssues },
                  { id: "restructuring", name: t.companyRestructuring },
                  { id: "misconduct", name: t.misconduct },
                  { id: "business_needs", name: t.businessNeeds },
                  { id: "other", name: t.other },
                ]}
                value={formData.demotionReason}
                onChange={(value) => handleDropdownChange("demotionReason", value)}
                placeholder={t.selectReason}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("demotionReason", "")}
              />
            </div>
          </>
        );

      case "transfer":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Building size={14} />
                {t.newDepartment} *
              </label>
              <ReusableDropdown
                items={departments.map((dept) => ({ id: dept, name: dept }))}
                value={formData.newDepartment}
                onChange={(value) => handleDropdownChange("newDepartment", value)}
                placeholder={t.selectDepartment}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("newDepartment", "")}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Briefcase size={14} />
                {t.newPosition}
              </label>
              <input
                type="text"
                name="newPosition"
                value={formData.newPosition}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.keepCurrentPosition}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Users size={14} />
                {t.newManager}
              </label>
              <ManagerAutocomplete
                value={formData.reportsTo}
                onChange={handleInputChange}
                theme={theme}
                styles={styles}
                language={language}
                t={t}
                managers={departmentManagers}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <MapPin size={14} />
                {t.transferType} *
              </label>
              <ReusableDropdown
                items={[
                  { id: "", name: t.selectType },
                  { id: "permanent", name: t.permanentTransfer },
                  { id: "temporary", name: t.temporaryTransfer },
                  { id: "project_based", name: t.projectBased },
                ]}
                value={formData.transferType}
                onChange={(value) => handleDropdownChange("transferType", value)}
                placeholder={t.selectType}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("transferType", "")}
              />
            </div>
          </>
        );

      case "salary_change":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <DollarSign size={14} />
                {isRTL ? "الراتب الإجمالي الجديد" : "New Gross Salary"}
              </label>
              <input
                type="number"
                name="newGrossSalary"
                value={formData.newGrossSalary}
                onChange={(e) => handleSalaryFieldChange("newGrossSalary", e.target.value)}
                readOnly={formData.salaryInputSource === "net"}
                disabled={formData.salaryInputSource === "net"}
                style={{
                  ...styles.input,
                  ...(formData.salaryInputSource === "net"
                    ? { opacity: 0.65, cursor: "not-allowed" }
                    : {}),
                }}
                className="enhanced-input-focus"
                placeholder={isRTL ? "أدخل الراتب الإجمالي الجديد" : "Enter new gross salary"}
                min="0"
                step="100"
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <DollarSign size={14} />
                {t.newSalary} *
              </label>
              <input
                type="number"
                name="newSalary"
                value={formData.newSalary}
                onChange={(e) => handleSalaryFieldChange("newSalary", e.target.value)}
                readOnly={formData.salaryInputSource === "gross"}
                disabled={formData.salaryInputSource === "gross"}
                style={{
                  ...styles.input,
                  ...(formData.salaryInputSource === "gross"
                    ? { opacity: 0.65, cursor: "not-allowed" }
                    : {}),
                }}
                className="enhanced-input-focus"
                placeholder={t.enterNewSalary}
                required
                min="0"
                step="100"
              />
              {selectedEmployee && formData.newSalary && (
                <div
                  style={{
                    fontSize: "clamp(11px, 1.6vw, 12px)",
                    color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                    marginTop: "6px",
                    fontWeight: "600",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t.currentSalary}: {t.egp}{" "}
                  {selectedEmployee.netSalary?.toLocaleString()} →{" "}
                  {t.newSalaryAmount}: {t.egp}{" "}
                  {parseFloat(formData.newSalary).toLocaleString()}
                  <span
                    style={{
                      color:
                        formData.newSalary > selectedEmployee.netSalary
                          ? "#10b981"
                          : "#ef4444",
                      marginLeft: isRTL ? "0" : "8px",
                      marginRight: isRTL ? "8px" : "0",
                    }}
                  >
                    (
                    {formData.newSalary > selectedEmployee.netSalary ? "+" : ""}
                    {(
                      ((formData.newSalary - selectedEmployee.netSalary) /
                        selectedEmployee.netSalary) *
                      100
                    ).toFixed(1)}
                    %)
                  </span>
                </div>
              )}
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Shield size={14} />
                {isRTL ? "الأجر التأميني الجديد" : "New Insurance Salary"}
              </label>
              <input
                type="number"
                name="newInsuranceSalary"
                value={formData.newInsuranceSalary ?? ""}
                onChange={(e) => handleInsuranceChange(e.target.value)}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={isRTL ? "أدخل الأجر التأميني الجديد" : "Enter new insurance salary"}
                min="0"
                step="100"
              />
              {selectedEmployee && (
                <div
                  style={{
                    fontSize: "clamp(11px, 1.6vw, 12px)",
                    color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                    marginTop: "6px",
                    fontWeight: "600",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {isRTL ? "الأجر التأميني الحالي" : "Current insurance salary"}: {t.egp}{" "}
                  {selectedEmployee.insuranceSalary != null
                    ? Number(selectedEmployee.insuranceSalary).toLocaleString()
                    : "—"}
                  {formData.newInsuranceSalary !== "" &&
                    formData.newInsuranceSalary != null &&
                    Number(formData.newInsuranceSalary) !== Number(selectedEmployee.insuranceSalary ?? NaN) && (
                      <span style={{ color: "#8b5cf6", marginInlineStart: 8 }}>
                        → {Number(formData.newInsuranceSalary).toLocaleString()}
                      </span>
                    )}
                </div>
              )}
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <TrendingUp size={14} />
                {t.adjustmentType} *
              </label>
              <ReusableDropdown
                items={[
                  { id: "", name: t.selectType },
                  { id: "annual_increase", name: t.annualIncrease },
                  { id: "promotion", name: t.promotion },
                  { id: "market_adjustment", name: t.marketAdjustment },
                  { id: "performance_bonus", name: t.performanceBased },
                  { id: "cost_of_living", name: t.costOfLiving },
                  { id: "correction", name: t.salaryCorrection },
                ]}
                value={formData.salaryAdjustmentType}
                onChange={(value) => handleDropdownChange("salaryAdjustmentType", value)}
                placeholder={t.selectType}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("salaryAdjustmentType", "")}
              />
            </div>
          </>
        );

      case "resignation":
      case "terminate":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <AlertCircle size={14} />
                {t.terminationType} *
              </label>
              <ReusableDropdown
                items={[
                  { id: "", name: t.selectType },
                  { id: "voluntary", name: t.voluntaryResignation },
                  { id: "involuntary", name: t.involuntaryTermination },
                  { id: "retirement", name: t.retirement },
                  { id: "contract_end", name: t.contractEnd },
                  { id: "mutual_agreement", name: t.mutualAgreement },
                  { id: "layoff", name: t.layoff },
                ]}
                value={formData.terminationType}
                onChange={(value) => handleDropdownChange("terminationType", value)}
                placeholder={t.selectType}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("terminationType", "")}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Calendar size={14} />
                {t.lastWorkingDay} *
              </label>
              <input
                type="date"
                name="lastWorkingDay"
                value={formData.lastWorkingDay}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                required
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <FileText size={14} />
                {t.terminationReason} *
              </label>
              <textarea
                name="terminationReason"
                value={formData.terminationReason}
                onChange={handleInputChange}
                style={styles.textarea}
                className="enhanced-input-focus"
                placeholder={t.provideDetailedTermination}
                required
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <DollarSign size={14} />
                {t.severancePackage}
              </label>
              <input
                type="number"
                name="severanceAmount"
                value={formData.severanceAmount}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterSeveranceAmount}
                min="0"
                step="100"
              />
            </div>
            {/*}
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Shield size={14} />
                {t.eligibleForRehire}
              </label>
              <ReusableDropdown
                items={[
                  { id: "yes", name: t.yes },
                  { id: "no", name: t.no },
                  { id: "conditional", name: t.conditional },
                ]}
                value={formData.eligibleForRehire}
                onChange={(value) => handleDropdownChange("eligibleForRehire", value)}
                placeholder={t.selectType}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("eligibleForRehire", "")}
              />
            </div>
            */}
          </>
        );

      case "suspend":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Clock size={14} />
                {t.suspensionStartDate} *
              </label>
              <input
                type="date"
                name="suspensionStartDate"
                value={formData.suspensionStartDate}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                required
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Clock size={14} />
                {t.suspensionEndDate} *
              </label>
              <input
                type="date"
                name="suspensionEndDate"
                value={formData.suspensionEndDate}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                required
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <AlertCircle size={14} />
                {t.suspensionType} *
              </label>
              <ReusableDropdown
                items={[
                  { id: "", name: t.selectType },
                  { id: "with_pay", name: t.withPay },
                  { id: "without_pay", name: t.withoutPay },
                  { id: "partial_pay", name: t.partialPay },
                ]}
                value={formData.suspensionType}
                onChange={(value) => handleDropdownChange("suspensionType", value)}
                placeholder={t.selectType}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("suspensionType", "")}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <FileText size={14} />
                {t.suspensionReason} *
              </label>
              <textarea
                name="suspensionReason"
                value={formData.suspensionReason}
                onChange={handleInputChange}
                style={styles.textarea}
                className="enhanced-input-focus"
                placeholder={t.provideDetailedSuspension}
                required
              />
            </div>
          </>
        );

      case "rehire":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Briefcase size={14} />
                {t.position} *
              </label>
              <input
                type="text"
                name="newPosition"
                value={formData.newPosition}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterPositionTitle}
                required
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Building size={14} />
                {t.department} *
              </label>
              <ReusableDropdown
                items={departments.map((dept) => ({ id: dept, name: dept }))}
                value={formData.newDepartment}
                onChange={(value) => handleDropdownChange("newDepartment", value)}
                placeholder={t.selectDepartment}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("newDepartment", "")}
              />
            </div>
            {!hideSalary && (
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <DollarSign size={14} />
                {t.startingSalary} *
              </label>
              <input
                type="number"
                name="newSalary"
                value={formData.newSalary}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterStartingSalary}
                required
                min="0"
                step="100"
              />
            </div>
            )}
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Calendar size={14} />
                {t.rehireDate} *
              </label>
              <input
                type="date"
                name="rehireDate"
                value={formData.rehireDate}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                required
              />
            </div>
          </>
        );
      /*
            case "contract":
              return (
                <>
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>
                      <FileCheck size={14} />
                      {t.contractType} *
                    </label>
                    <ReusableDropdown
                      items={[
                        { id: "", name: t.selectContractType },
                        { id: "permanent", name: t.permanent },
                        { id: "fixed_term", name: t.fixedTerm },
                        { id: "temporary", name: t.temporary },
                        { id: "part_time", name: t.partTime },
                        { id: "consultant", name: t.consultant },
                        { id: "contractor", name: t.contractor },
                      ]}
                      value={formData.contractType}
                      onChange={(value) => handleDropdownChange("contractType", value)}
                      placeholder={t.selectContractType}
                      getItemLabel={(item) => item.name}
                      getItemKey={(item) => item.id}
                      theme={theme}
                      language={language}
                      maxItems={10}
                      onClear={() => handleDropdownChange("contractType", "")}
                    />
                  </div>
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>
                      <Calendar size={14} />
                      {t.contractStartDate} *
                    </label>
                    <input
                      type="date"
                      name="contractStartDate"
                      value={formData.contractStartDate}
                      onChange={handleInputChange}
                      style={styles.input}
                      className="enhanced-input-focus"
                      required
                    />
                  </div>
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>
                      <Calendar size={14} />
                      {t.contractEndDate}
                    </label>
                    <input
                      type="date"
                      name="contractEndDate"
                      value={formData.contractEndDate}
                      onChange={handleInputChange}
                      style={styles.input}
                      className="enhanced-input-focus"
                      min={formData.contractStartDate}
                    />
                  </div>
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>
                      <FileText size={14} />
                      {t.contractTerms}
                    </label>
                    <textarea
                      name="contractTerms"
                      value={formData.contractTerms}
                      onChange={handleInputChange}
                      style={styles.textarea}
                      className="enhanced-input-focus"
                      placeholder={t.enterContractTerms}
                    />
                  </div>
                </>
              );
      */
      case "rotation":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Building size={14} />
                {t.rotationDepartment} *
              </label>
              <ReusableDropdown
                items={[
                  { id: "", name: t.selectDepartment },
                  ...departments.map((dept) => ({ id: dept, name: dept })),
                ]}
                value={formData.rotationDepartment}
                onChange={(value) => handleDropdownChange("rotationDepartment", value)}
                placeholder={t.selectDepartment}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("rotationDepartment", "")}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Briefcase size={14} />
                {t.rotationRole} *
              </label>
              <input
                type="text"
                name="rotationRole"
                value={formData.rotationRole}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterRotationRole}
                required
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Clock size={14} />
                {t.durationMonths} *
              </label>
              <input
                type="number"
                name="rotationDuration"
                value={formData.rotationDuration}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterDuration}
                required
                min="1"
                max="24"
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Target size={14} />
                {t.rotationObjectives}
              </label>
              <textarea
                name="rotationObjectives"
                value={formData.rotationObjectives}
                onChange={handleInputChange}
                style={styles.textarea}
                className="enhanced-input-focus"
                placeholder={t.enterObjectives}
              />
            </div>
          </>
        );

      case "review":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Star size={14} />
                {t.performanceRating} *
              </label>
              <ReusableDropdown
                items={[
                  { id: "", name: t.selectRating },
                  { id: "Excellent", name: t.excellent },
                  { id: "Very Good", name: t.veryGood },
                  { id: "Good", name: t.good },
                  { id: "Average", name: t.average },
                  { id: "Below Average", name: t.belowAverage },
                  { id: "Poor", name: t.poor },
                ]}
                value={formData.performanceRating}
                onChange={(value) => handleDropdownChange("performanceRating", value)}
                placeholder={t.selectRating}
                getItemLabel={(item) => item.name}
                getItemKey={(item) => item.id}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => handleDropdownChange("performanceRating", "")}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <BarChart3 size={14} />
                {t.reviewScore} *
              </label>
              <input
                type="number"
                name="reviewScore"
                value={formData.reviewScore}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterScore}
                required
                min="1"
                max="10"
                step="0.1"
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <TrendingUp size={14} />
                {t.strengths}
              </label>
              <textarea
                name="strengths"
                value={formData.strengths}
                onChange={handleInputChange}
                style={styles.textarea}
                className="enhanced-input-focus"
                placeholder={t.listStrengths}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Target size={14} />
                {t.areasForImprovement}
              </label>
              <textarea
                name="improvements"
                value={formData.improvements}
                onChange={handleInputChange}
                style={styles.textarea}
                className="enhanced-input-focus"
                placeholder={t.listImprovements}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Calendar size={14} />
                {t.nextReviewDate}
              </label>
              <input
                type="date"
                name="nextReviewDate"
                value={formData.nextReviewDate}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
              />
            </div>
          </>
        );

      case "training":
        return (
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <GraduationCap size={14} />
                {t.trainingProgram} *
              </label>
              <input
                type="text"
                name="trainingProgram"
                value={formData.trainingProgram}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterTrainingProgram}
                required
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Calendar size={14} />
                {t.trainingStartDate} *
              </label>
              <input
                type="date"
                name="trainingStartDate"
                value={formData.trainingStartDate}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                required
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Calendar size={14} />
                {t.trainingEndDate} *
              </label>
              <input
                type="date"
                name="trainingEndDate"
                value={formData.trainingEndDate}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                required
                min={formData.trainingStartDate}
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <DollarSign size={14} />
                {t.trainingCost}
              </label>
              <input
                type="number"
                name="trainingCost"
                value={formData.trainingCost}
                onChange={handleInputChange}
                style={styles.input}
                className="enhanced-input-focus"
                placeholder={t.enterTrainingCost}
                min="0"
                step="100"
              />
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <Target size={14} />
                {t.trainingObjectives}
              </label>
              <textarea
                name="trainingObjectives"
                value={formData.trainingObjectives}
                onChange={handleInputChange}
                style={styles.textarea}
                className="enhanced-input-focus"
                placeholder={t.enterTrainingObjectives}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.formGrid}>
      {renderActionSpecificFields()}

      {/* Common fields */}
      {selectedAction !== "suspend" && selectedAction !== "terminate" && selectedAction !== "resignation" && (
        <div style={styles.controlGroup}>
          <label style={styles.label}>
            <Calendar size={14} />
            {t.effectiveDate} *
          </label>
          <input
            type="date"
            name="effectiveDate"
            value={formData.effectiveDate}
            onChange={handleInputChange}
            style={styles.input}
            className="enhanced-input-focus"
            required
          />
        </div>
      )}

      <div style={styles.controlGroup}>
        <label style={styles.label}>
          <FileText size={14} />
          {t.reasonForAction} *
        </label>
        <textarea
          name="reason"
          value={formData.reason}
          onChange={handleInputChange}
          style={styles.textarea}
          className="enhanced-input-focus"
          placeholder={t.provideDetailedReason}
          required
        />
      </div>

      <div style={styles.controlGroup}>
        <label style={styles.label}>
          <FileText size={14} />
          {t.approverNotes} *
        </label>
        <textarea
          name="approverNotes"
          value={formData.approverNotes}
          onChange={handleInputChange}
          style={styles.textarea}
          className="enhanced-input-focus"
          placeholder={t.addAdditionalNotes}
          required
        />
      </div>

      {actionConfig.requiresApproval && (
        <div style={styles.controlGroup}>
          <label style={styles.label}>
            <Shield size={14} />
            {t.approvalRequired}
          </label>
          <div
            style={{
              background:
                theme === "dark"
                  ? "rgba(59, 130, 246, 0.1)"
                  : "rgba(59, 130, 246, 0.05)",
              padding: "clamp(14px, 2.2vw, 16px)",
              borderRadius: "clamp(10px, 1.5vw, 12px)",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              fontSize: "clamp(12px, 1.7vw, 14px)",
              color: theme === "dark" ? "rgba(255,255,255,0.8)" : "#374151",
              fontWeight: "600",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: isRTL ? "row" : "row",
                alignItems: "center",
                gap: "8px",
                marginBottom: "6px",
              }}
            >
              <ShieldCheck size={16} color="#2e7cba" />
              <span style={{ fontWeight: "700", color: "#2e7cba" }}>
                {t.approvalRequired}
              </span>
            </div>
            {t.thisActionRequires}{" "}
            <strong>{getWorkflowApprovalLabel(
              actionConfig.id,
              workflowDefinitions,
              language,
              t,
              actionConfig.approvalLevelKey,
              selectedEmployee?.department?.name ?? selectedEmployee?.employeeType ?? null
            )}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionFormFields;